import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, CheckCircle2, Plus, Truck } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { EmptyState, PageHeader, StatusBadge, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import AddressFields, { EMPTY_ADDRESS, addressFromParty, addressText } from "../components/AddressFields";

const NEXT = { pending: ["dispatched", "cancelled"], dispatched: ["in_transit", "delivered"], in_transit: ["delivered"] };
const STATUS_LABEL = { pending: "Pending", dispatched: "Dispatched", in_transit: "In Transit", delivered: "Delivered", cancelled: "Cancelled" };

export default function Delivery() {
  const { activeId } = useCompany();
  const [data, setData] = useState(null);
  const [dialog, setDialog] = useState(false);
  const [parties, setParties] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [form, setForm] = useState({ party_id: "", invoice_id: "", date: new Date().toISOString().slice(0, 10),
    vehicle: "", driver: "", receiver: "", delivery_address: "", shipping_address_details: EMPTY_ADDRESS, notes: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    api.get("/delivery-challans?limit=200").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  };
  useEffect(() => { if (activeId) { setData(null); load(); } }, [activeId]); // eslint-disable-line

  useEffect(() => {
    if (!dialog) return;
    api.get("/parties?limit=200").then((r) => setParties(r.data.items.filter((p) => !p.archived))).catch(() => {});
  }, [dialog]);

  useEffect(() => {
    if (!form.party_id) { setInvoices([]); return; }
    api.get(`/invoices?party_id=${form.party_id}&status=posted&limit=100`).then((r) => setInvoices(r.data.items)).catch(() => {});
  }, [form.party_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    const p = parties.find((x) => x.id === form.party_id);
    if (p) { const a = addressFromParty(p, "shipping"); const fallback = a.line1 ? a : addressFromParty(p, "billing"); set("shipping_address_details", fallback); set("delivery_address", addressText(fallback)); }
  }, [form.party_id, parties]);

  const save = async () => {
    if (!form.party_id) { toast.error("Select a customer"); return; }
    setBusy(true);
    try {
      await api.post("/delivery-challans", { ...form, invoice_id: form.invoice_id || null, items: [], delivery_address: addressText(form.shipping_address_details) || form.delivery_address });
      toast.success("Delivery challan created");
      setDialog(false);
      setForm({ party_id: "", invoice_id: "", date: new Date().toISOString().slice(0, 10), vehicle: "", driver: "", receiver: "", delivery_address: "", shipping_address_details: EMPTY_ADDRESS, notes: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const advance = async (dc, status) => {
    try {
      await api.post(`/delivery-challans/${dc.id}/status`, { status });
      toast.success(`${dc.doc_no} → ${STATUS_LABEL[status]}`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="delivery-page">
      <PageHeader title="Delivery Challans" testId="delivery-title" sub="Track material movement to customer sites">
        <Button data-testid="new-dc-btn" onClick={() => setDialog(true)}
          className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900"><Plus className="mr-1.5 h-4 w-4" /> New Challan</Button>
      </PageHeader>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
        {!data ? <TableSkeleton /> : data.items.length === 0 ? (
          <EmptyState testId="delivery-empty" title="No delivery challans" body="Create a challan from a posted invoice to dispatch material." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="delivery-table">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-3">Challan No</th><th className="p-3">Date</th><th className="p-3">Customer</th>
                <th className="p-3">Invoice</th><th className="p-3">Items</th><th className="p-3">Vehicle / Driver</th>
                <th className="p-3">Status</th><th className="p-3"></th></tr></thead>
              <tbody>
                {data.items.map((dc) => (
                  <tr key={dc.id} data-testid={`dc-row-${dc.doc_no}`} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                    <td className="p-3 font-mono text-xs font-semibold">{dc.doc_no}</td>
                    <td className="p-3">{fmtDate(dc.date)}</td>
                    <td className="p-3 max-w-[160px] truncate">{dc.party_name}</td>
                    <td className="p-3 font-mono text-xs text-slate-500">{dc.invoice_no || "—"}</td>
                    <td className="p-3 text-xs text-slate-500 max-w-[200px] truncate">{dc.items.map((i) => `${i.name} ×${i.qty}`).join(", ")}</td>
                    <td className="p-3 text-xs text-slate-500">{[dc.vehicle, dc.driver].filter(Boolean).join(" / ") || "—"}</td>
                    <td className="p-3"><StatusBadge status={STATUS_LABEL[dc.status]} /></td>
                    <td className="p-3">
                      <div className="flex gap-1 justify-end">
                        {(NEXT[dc.status] || []).map((s) => (
                          <Button key={s} size="sm" variant={s === "cancelled" ? "ghost" : "outline"}
                            data-testid={`dc-${s}-${dc.doc_no}`} onClick={() => advance(dc, s)}
                            className={s === "cancelled" ? "text-red-500 h-7" : "h-7"}>
                            {s === "delivered" ? <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> : s === "cancelled" ? <Ban className="h-3.5 w-3.5" /> : <Truck className="mr-1 h-3.5 w-3.5" />}
                            {s !== "cancelled" ? STATUS_LABEL[s] : ""}
                          </Button>
                        ))}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent aria-describedby={undefined} className="max-w-lg bg-white dark:bg-slate-900" data-testid="dc-dialog">
          <DialogHeader><DialogTitle className="font-display">New Delivery Challan</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2"><Label>Customer *</Label>
              <Select value={form.party_id} onValueChange={(v) => set("party_id", v)}>
                <SelectTrigger data-testid="dc-party" className="mt-1.5"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Against Invoice (optional — items auto-fill)</Label>
              <Select value={form.invoice_id} onValueChange={(v) => set("invoice_id", v === "none" ? "" : v)}>
                <SelectTrigger data-testid="dc-invoice" className="mt-1.5"><SelectValue placeholder="Select posted invoice" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No invoice link —</SelectItem>
                  {invoices.map((i) => <SelectItem key={i.id} value={i.id}>{i.invoice_no} ({fmtDate(i.date)})</SelectItem>)}
                </SelectContent>
              </Select>
              {!form.invoice_id && <p className="mt-1 text-xs text-amber-600">Without an invoice link, add items via a linked invoice — challans track invoice deliveries.</p>}
            </div>
            <div><Label>Date</Label><Input type="date" className="mt-1.5" value={form.date} onChange={(e) => set("date", e.target.value)} /></div>
            <div><Label>Vehicle No</Label><Input data-testid="dc-vehicle" className="mt-1.5" value={form.vehicle} onChange={(e) => set("vehicle", e.target.value)} /></div>
            <div><Label>Driver</Label><Input className="mt-1.5" value={form.driver} onChange={(e) => set("driver", e.target.value)} /></div>
            <div><Label>Receiver Name</Label><Input className="mt-1.5" value={form.receiver} onChange={(e) => set("receiver", e.target.value)} /></div>
            <div className="col-span-2"><AddressFields title="Shipping / Delivery Address" value={form.shipping_address_details} onChange={(v) => { set("shipping_address_details", v); set("delivery_address", addressText(v)); }} icon="shipping" copyLabel="Use Billing Address" onCopy={() => { const p = parties.find((x) => x.id === form.party_id); if (p) { const a = addressFromParty(p, "billing"); set("shipping_address_details", a); set("delivery_address", addressText(a)); } }} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} className="mt-1.5" value={form.notes} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button data-testid="dc-save-btn" onClick={save} disabled={busy || !form.invoice_id}
              className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Create Challan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
